/**
 * The sweep-finished entry points — V109-5-2 / 5-4 (G-8: each view ≤ 2 clicks from a sweep finishing;
 * PD-6: every workspace reaches the inventory).
 *
 *   SweepNotice     Today's "Sweep" section: "Sweep finished · 32 found" (→ Reveal) and one link per
 *                   count, each opening its own view and program-aware (round 3 #5): "GovRAMP · 2 broke ·
 *                   2 couldn't check" → that program's Changes in its current stage; "4 services need a
 *                   verdict" → Verdicts; "2 clues to confirm" → the inventory filtered to them; "Open
 *                   GovRAMP" → the roll-up of the program something broke in. Never a filled primary
 *                   (Today keeps its one).
 *   SweepToast      the DS Toast once per finished sweep (per viewer, browser storage; it never
 *                   auto-dismisses). Its height is published as --toast-h through a ResizeObserver and it
 *                   sits above the mobile tab bar (DS punch #1); the text is announced, never focus-stealing.
 *   SweepSetupRow   Setup's row: "Sweep finished · <sources> · <time>" with "See everything found".
 *
 * Reads (any member): GET /sweeps/summary and GET /programs (cards, for each program's current stage).
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Toast } from '@aigovops/design-system';
import type { SweepSummary } from '@aigovops/core';
import { getSweepSummary, sweepKeys } from '../../lib/sweep-api';
import { changesHref, clockText, foundText, freshness, inventoryHref, leadSweepProgram, nextProgramFor, programHref, currentStage, verdictsHref, type CardLike } from '../../lib/sweep-model';
import { programLabel } from '../../lib/boundary-model';
import { usePrograms } from '../../lib/workspace-navigation';
import { sourceLabel } from './SweepFreshness';
import './sweep.css';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function useSweepSummary(slug: string) {
  return useQuery({ queryKey: sweepKeys.summary(slug), queryFn: () => getSweepSummary(slug), staleTime: 30_000, retry: false });
}

export interface NoticeLink { key: string; text: string; href: string }

/** Pure: the notice's links, each to its own view (exported for tests). */
export function noticeLinks(s: SweepSummary, cards: readonly CardLike[], prefix: string): { title: NoticeLink; links: NoticeLink[]; summary: NoticeLink | null } {
  const lead = leadSweepProgram(s);
  const card = (k: string): CardLike => cards.find((c) => c.program === k) ?? { program: k, periods: [] };
  const links: NoticeLink[] = [];
  for (const p of s.programs) {
    const parts = [p.broke ? `${p.broke} broke` : '', p.couldnt_check ? `${p.couldnt_check} couldn't check` : ''].filter(Boolean);
    if (!parts.length) continue;
    links.push({ key: `p-${p.program}`, text: `${programLabel(p.program)} · ${parts.join(' · ')}`, href: changesHref(prefix, card(p.program)) });
  }
  if (s.verdicts_need_you && lead) links.push({ key: 'verdicts', text: `${plural(s.verdicts_need_you, 'service')} need${s.verdicts_need_you === 1 ? 's' : ''} a verdict`, href: verdictsHref(prefix, lead) });
  if (s.clues_open) links.push({ key: 'clues', text: `${plural(s.clues_open, 'clue')} to confirm`, href: inventoryHref(prefix, lead, { show: 'unresolved' }) });
  const next = nextProgramFor(s);
  // Built-UI #1: "N found" is Reveal's count (the clues ride beside it as their own link); Nit: the action names its program.
  return {
    title: { key: 'found', text: `Sweep finished · ${s.found} found`, href: inventoryHref(prefix, lead) },
    links,
    summary: next ? { key: 'summary', text: `Open ${programLabel(next)}`, href: programHref(prefix, next, currentStage(card(next))) } : null,
  };
}

export function SweepNotice({ slug, prefix }: { slug: string; prefix: string }) {
  const q = useSweepSummary(slug);
  const cards = usePrograms(slug).data?.programs ?? [];
  const s = q.data;
  if (!s?.run) return null;
  const n = noticeLinks(s, cards, prefix);
  const f = freshness(s.run, s.compared_with);
  const next = new Date(new Date(s.run.started_at).getTime() + 24 * 3_600_000).toISOString();
  return (
    <section className="gp-sec" aria-labelledby="sweep-h" data-part="sweep-notice">
      <div className="gp-sec-head">
        <h2 id="sweep-h" className="gp-sec-h">Sweep</h2>
        <span className="gp-sec-count">{`${f.text.replace(/^Swept /, '')} · next ${clockText(next)}`}</span>
      </div>
      <div className="sw-band" data-tone={s.programs.some((p) => p.broke) ? 'bad' : n.links.length ? 'warn' : undefined}>
        <span className="sw-band-main sw-notice">
          <Link className="gp-ow-t" to={n.title.href}>{n.title.text}</Link>
          {n.links.length ? <span className="gp-ow-meta">{n.links.map((l, i) => <span key={l.key}>{i ? <span aria-hidden="true">{' · '}</span> : null}<Link to={l.href}>{l.text}</Link></span>)}</span> : <span className="sw-dim">Nothing broke since the last sweep.</span>}
        </span>
        {n.summary ? <Link className="gp-linkbtn gp-linkbtn--compact" to={n.summary.href}>{n.summary.text}</Link> : null}
      </div>
    </section>
  );
}

const seenKey = (slug: string) => `gp-sweep-seen:${slug}`;
const readSeen = (slug: string): string | null => { try { return window.localStorage.getItem(seenKey(slug)); } catch { return null; } };
const writeSeen = (slug: string, runId: string) => { try { window.localStorage.setItem(seenKey(slug), runId); } catch { /* storage unavailable: the toast just shows again */ } };

/** The sweep-finished toast, once per finished sweep for this viewer. */
export function SweepToast({ slug, prefix }: { slug: string; prefix: string }) {
  const q = useSweepSummary(slug);
  const cards = usePrograms(slug).data?.programs ?? [];
  const navigate = useNavigate();
  const [seen, setSeen] = useState<string | null>(() => readSeen(slug));
  useEffect(() => { setSeen(readSeen(slug)); }, [slug]);
  const s = q.data;
  if (!s?.run || seen === s.run.run_id) return null;
  const n = noticeLinks(s, cards, prefix);
  const broke = s.programs.filter((p) => p.broke).map((p) => `${programLabel(p.program)} ${p.broke} broke`);
  const message = [`Sweep finished · ${foundText(s.found, s.clues_open)}`, ...broke].join(' · ');
  const dismiss = () => { writeSeen(slug, s.run!.run_id); setSeen(s.run!.run_id); };
  const go = (href: string) => { dismiss(); navigate(href); };
  return <Toast message={message} details={n.links.map((l) => l.text).join('. ') || undefined}
    action={n.summary ? { label: n.summary.text, onClick: () => go(n.summary!.href) } : { label: 'See everything', onClick: () => go(n.title.href) }}
    onDismiss={dismiss} returnFocus={document.querySelector<HTMLElement>('h1')} />;
}

/** Setup's row (PD-6): the inventory is one click from Setup for every workspace. */
export function SweepSetupRow({ slug, prefix }: { slug: string; prefix: string }) {
  const q = useSweepSummary(slug);
  const s = q.data;
  if (!s?.run) return null;
  const names = [...new Set(s.sources.map((x) => (x.source_kind === 'repo_scan' ? 'code' : sourceLabel(x))))].join(' · ');
  return (
    <section className="su-card" aria-labelledby="su-sweep-h" data-part="setup-sweep">
      <div className="sw-band">
        <span className="sw-band-main"><b id="su-sweep-h">{`Sweep finished · ${foundText(s.found, s.clues_open)}`}</b><span className="sw-dim">{[names, clockText(s.run.finished_at ?? s.run.started_at)].filter(Boolean).join(' · ')}</span></span>
        <Link className="gp-linkbtn gp-linkbtn--compact" to={inventoryHref(prefix, leadSweepProgram(s))}>See everything found</Link>
      </div>
    </section>
  );
}
